import { ROUTE_PAYMENT, ROUTE_QUESTIONNAIRE, ROUTE_QUESTIONNAIRE_V2 } from '@/constants/routes';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import useOnMount from '@/hooks/useOnMount';
import useVisitorSession from '@/hooks/useVisitorSession';

import authService from '@/services/auth.service';
import { useToast } from '@cmpsr/components';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function useSignUpController() {
  const navigate = useNavigate();
  const { captureLead, initializeSession, email } = useVisitorSession();
  const form = useForm({ mode: 'onSubmit', reValidateMode: 'onChange', defaultValues: { email: '' } });
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [isInviteCodeLoaded, setIsInviteCodeLoaded] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | undefined>(undefined);

  const handleCta = useCallback(() => {
    // Preserve query parameters when navigating to payment
    const queryString = searchParams.toString();
    const paymentUrl = queryString ? `${ROUTE_PAYMENT}?${queryString}` : ROUTE_PAYMENT;
    navigate(paymentUrl, { replace: true });
  }, [navigate, searchParams]);

  useOnMount(() => {
    document.title = 'Sign Up';
    initializeSession();
    
    // Load invite code from URL params or storage
    const urlInviteCode = searchParams.get('invite-code');
    const storedInviteCode = authService.getInviteCode();
    const finalInviteCode = urlInviteCode || storedInviteCode || undefined;
    
    setInviteCode(finalInviteCode);
    setIsInviteCodeLoaded(true);
  });

  // If the user has an email, redirect to payment
  // Unless they have an invite code containing VIP with _ or - (case insensitive)
  useEffect(() => {
    // Don't redirect until we've checked for invite code
    if (!isInviteCodeLoaded) return;
    
    const hasVipCode = inviteCode && (
      inviteCode.toLowerCase().startsWith('vip_') ||
      inviteCode.toLowerCase().startsWith('vip-') ||
      inviteCode.toLowerCase().endsWith('-vip') ||
      inviteCode.toLowerCase().endsWith('_vip')
    );
    if (email && !hasVipCode) {
      handleCta();
    }
  }, [email, inviteCode, handleCta, isInviteCodeLoaded]);

  function handleBack() {
    return navigate(-1);
  }

  const handleSubmit = form.handleSubmit((values) => {
    // Use the latest invite code from state
    const currentInviteCode = inviteCode || authService.getInviteCode() || undefined;
    
    captureLead(values.email, currentInviteCode)
      .then((res) => {
        // Check if user has VIP access and a token
        if (res.hasVipAccess && res.token) {
          // Store the token to authenticate the user
          authService.login({ token: res.token });
          // Redirect VIP users to questionnaire
          navigate(ROUTE_QUESTIONNAIRE_V2, { replace: true });
        } else {
          // Regular users go to payment page
          handleCta();
        }
      })
      .catch((error) => {
        console.error(error);
        toast({
          ...TOAST_DEFAULT_OPTIONS,
          title: `Error ${error.response?.data?.statusCode ?? ''}`,
          description: error.response?.data?.message ?? error.message,
          status: 'error',
        });
      });
  });

  return {
    form,
    handleSubmit,
    handleBack,
  };
}
